/**
 * AgentBase Embed SDK
 *
 * Lightweight JavaScript SDK for embedding AgentBase apps on external websites.
 *
 * @example
 * ```html
 * <div id="agentbase-widget"></div>
 * <script src="https://agentbase.dk/embed.js"></script>
 * <script>
 *   AgentBase.mount('#agentbase-widget', {
 *     appId: 'xxx',
 *     token: 'emb_xxx',
 *     theme: { mode: 'light', primaryColor: '#3b82f6' },
 *   });
 * </script>
 * ```
 */

import { render } from 'preact'
import { Widget } from './components/Widget'
import { generateWidgetCSS } from './core/theme'
import type { AgentBaseConfig, AgentBaseWidget } from './types'

declare const __VERSION__: string

// Generate unique instance ID
function generateInstanceId(): string {
  return `syv-${Math.random().toString(36).slice(2, 9)}`
}

// Inject styles into document head
function injectStyles(instanceId: string, css: string): HTMLStyleElement {
  const style = document.createElement('style')
  style.id = `${instanceId}-styles`
  style.textContent = css
  document.head.appendChild(style)
  return style
}

// Remove injected styles
function removeStyles(instanceId: string): void {
  const style = document.getElementById(`${instanceId}-styles`)
  if (style) {
    style.remove()
  }
}

/**
 * Mount a AgentBase widget to a container element.
 *
 * @param container - CSS selector or HTMLElement to mount into
 * @param config - Widget configuration
 * @returns Widget controller object
 */
function mount(
  container: string | HTMLElement,
  config: AgentBaseConfig
): AgentBaseWidget {
  // Resolve container element
  const element =
    typeof container === 'string'
      ? document.querySelector<HTMLElement>(container)
      : container

  if (!element) {
    throw new Error(`AgentBase: Container not found: ${container}`)
  }

  // Validate required config
  if (!config.appId) {
    throw new Error('AgentBase: appId is required')
  }
  if (!config.token) {
    throw new Error('AgentBase: token is required')
  }

  // Generate unique instance ID and inject styles
  const instanceId = generateInstanceId()
  const css = generateWidgetCSS(instanceId, config.theme || {})
  injectStyles(instanceId, css)

  // Add instance class and layout class to container
  element.classList.add(instanceId)
  if (config.layout) {
    element.classList.add(`syv-layout-${config.layout}`)
  }

  // Create widget state that will be controlled by the Preact component
  let widgetApi: {
    execute: () => void
    reset: () => void
    setInput: (name: string, value: unknown) => void
    getInputs: () => Record<string, unknown>
    isExecuting: () => boolean
    getResults: () => Record<string, unknown> | null
  } | null = null

  // Render the Preact component
  render(
    <Widget
      config={config}
      instanceId={instanceId}
      onApiReady={(api) => {
        widgetApi = api
      }}
    />,
    element
  )

  // Return controller object
  const widget: AgentBaseWidget = {
    get isExecuting() {
      return widgetApi?.isExecuting() ?? false
    },
    get lastResults() {
      return widgetApi?.getResults() ?? null
    },
    execute() {
      widgetApi?.execute()
    },
    reset() {
      widgetApi?.reset()
    },
    setInput(name: string, value: unknown) {
      widgetApi?.setInput(name, value)
    },
    getInputs() {
      return widgetApi?.getInputs() ?? {}
    },
    destroy() {
      render(null, element)
      removeStyles(instanceId)
      element.classList.remove(instanceId)
      if (config.layout) {
        element.classList.remove(`syv-layout-${config.layout}`)
      }
    },
  }

  return widget
}

/**
 * AgentBase SDK version
 */
const version = typeof __VERSION__ !== 'undefined' ? __VERSION__ : '0.1.0'

// Export the SDK
// eslint-disable-next-line react-refresh/only-export-components
export const AgentBase = {
  mount,
  version,
}

// Deprecated alias for the previous SDK name. Kept so ES-module importers that
// still `import { SyvFlows }` keep working; the IIFE global is now `AgentBase`.
export const SyvFlows = AgentBase

// Also export individual functions for ES module usage
// eslint-disable-next-line react-refresh/only-export-components
export { mount, version }

// Export types for TypeScript users
export type { AgentBaseConfig, AgentBaseWidget, ThemeConfig } from './types'

// Default export for IIFE/UMD
export default AgentBase
